import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
  ScatterChart,
  Scatter,
  Cell
} from 'recharts';

interface FilterProps {
  dateRange: { start: string; end: string; preset: string };
  departments: string[];
  metrics: string[];
  detailLevel: string;
  displayType: string;
  currency: string;
}

interface TrendAnalysisProps {
  filters: FilterProps;
}

const TrendAnalysis: React.FC<TrendAnalysisProps> = ({ filters }) => {
  const { t, direction } = useLanguage();
  const [selectedPeriod, setSelectedPeriod] = useState('6months');
  const [trendType, setTrendType] = useState('revenue');

  // Historical trend data (12 months)
  const historicalData = [
    { month: 'يناير 2024', revenue: 1850000, expenses: 1295000, profit: 555000, growth: -2.1, customers: 1250, orders: 3420 },
    { month: 'فبراير 2024', revenue: 1920000, expenses: 1344000, profit: 576000, growth: 3.8, customers: 1280, orders: 3650 },
    { month: 'مارس 2024', revenue: 2100000, expenses: 1470000, profit: 630000, growth: 9.4, customers: 1350, orders: 3890 },
    { month: 'أبريل 2024', revenue: 2050000, expenses: 1435000, profit: 615000, growth: -2.4, customers: 1320, orders: 3750 },
    { month: 'مايو 2024', revenue: 2200000, expenses: 1540000, profit: 660000, growth: 7.3, customers: 1420, orders: 4100 },
    { month: 'يونيو 2024', revenue: 2350000, expenses: 1645000, profit: 705000, growth: 6.8, customers: 1480, orders: 4320 },
    { month: 'يوليو 2024', revenue: 2280000, expenses: 1596000, profit: 684000, growth: -3.0, customers: 1450, orders: 4180 },
    { month: 'أغسطس 2024', revenue: 2420000, expenses: 1694000, profit: 726000, growth: 6.1, customers: 1520, orders: 4450 },
    { month: 'سبتمبر 2024', revenue: 2380000, expenses: 1666000, profit: 714000, growth: -1.7, customers: 1500, orders: 4380 },
    { month: 'أكتوبر 2024', revenue: 2550000, expenses: 1785000, profit: 765000, growth: 7.1, customers: 1580, orders: 4650 },
    { month: 'نوفمبر 2024', revenue: 2650000, expenses: 1855000, profit: 795000, growth: 3.9, customers: 1620, orders: 4820 },
    { month: 'ديسمبر 2024', revenue: 2847500, expenses: 1993250, profit: 854250, growth: 7.5, customers: 1680, orders: 5100 }
  ];

  // Trend indicators
  const trendIndicators = [
    {
      title: 'نمو الإيرادات',
      value: '+18.5%',
      trend: 'up',
      period: 'آخر 6 أشهر',
      prediction: '+22%',
      confidence: 85,
      icon: ArrowTrendingUpIcon,
      color: 'from-green-500 to-green-600',
      status: 'excellent'
    },
    {
      title: 'تطور الأرباح',
      value: '+24.2%',
      trend: 'up',
      period: 'آخر 6 أشهر',
      prediction: '+28%',
      confidence: 78,
      icon: ChartBarIcon,
      color: 'from-blue-500 to-blue-600',
      status: 'excellent'
    },
    {
      title: 'نمو قاعدة العملاء',
      value: '+13.6%',
      trend: 'up',
      period: 'آخر 6 أشهر',
      prediction: '+16%',
      confidence: 92,
      icon: EyeIcon,
      color: 'from-purple-500 to-purple-600',
      status: 'good'
    },
    {
      title: 'زيادة الطلبات',
      value: '+31.8%',
      trend: 'up',
      period: 'آخر 6 أشهر',
      prediction: '+35%',
      confidence: 88,
      icon: CursorArrowRaysIcon,
      color: 'from-orange-500 to-orange-600',
      status: 'excellent'
    }
  ];

  // Seasonal patterns
  const seasonalPatterns = [
    {
      season: 'الربع الأول',
      avgGrowth: 3.7,
      pattern: 'نمو متوسط',
      description: 'بداية قوية مع تحسن تدريجي',
      recommendation: 'التركيز على الاستحواذ على عملاء جدد'
    },
    {
      season: 'الربع الثاني',
      avgGrowth: 6.8,
      pattern: 'نمو قوي',
      description: 'أفضل أداء في السنة',
      recommendation: 'زيادة الاستثمار في التسويق'
    },
    {
      season: 'الربع الثالث',
      avgGrowth: 0.5,
      pattern: 'نمو بطيء',
      description: 'فترة هدوء نسبي',
      recommendation: 'التركيز على تحسين الكفاءة'
    },
    {
      season: 'الربع الرابع',
      avgGrowth: 5.8,
      pattern: 'نمو متسارع',
      description: 'نهاية قوية للسنة',
      recommendation: 'الاستعداد للسنة القادمة'
    }
  ];

  // Future predictions
  const predictions = [
    { month: 'يناير 2025', predicted: 3050000, confidence: 85, lower: 2900000, upper: 3200000 },
    { month: 'فبراير 2025', predicted: 3180000, confidence: 82, lower: 3000000, upper: 3360000 },
    { month: 'مارس 2025', predicted: 3350000, confidence: 78, lower: 3150000, upper: 3550000 },
    { month: 'أبريل 2025', predicted: 3280000, confidence: 75, lower: 3080000, upper: 3480000 },
    { month: 'مايو 2025', predicted: 3520000, confidence: 72, lower: 3300000, upper: 3740000 },
    { month: 'يونيو 2025', predicted: 3680000, confidence: 70, lower: 3450000, upper: 3910000 }
  ];

  // Key insights
  const keyInsights = [
    {
      type: 'positive',
      title: 'نمو مستدام في الإيرادات',
      description: 'الإيرادات تنمو بمعدل ثابت 18.5% مع توقعات إيجابية للأشهر القادمة',
      impact: 'عالي',
      action: 'مواصلة الاستراتيجية الحالية'
    },
    {
      type: 'warning',
      title: 'تقلبات في الربع الثالث',
      description: 'انخفاض طفيف في الأداء خلال أشهر الصيف يتطلب خطة موسمية',
      impact: 'متوسط',
      action: 'تطوير استراتيجية موسمية'
    },
    {
      type: 'positive',
      title: 'نمو قاعدة العملاء',
      description: 'زيادة مستمرة في عدد العملاء الجدد بمعدل 13.6%',
      impact: 'عالي',
      action: 'تحسين تجربة العملاء'
    },
    {
      type: 'opportunity',
      title: 'فرصة في الربع الرابع',
      description: 'الربع الرابع يظهر أداءً قوياً يمكن الاستفادة منه أكثر',
      impact: 'متوسط',
      action: 'زيادة الاستثمار التسويقي'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-300';
      case 'good':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'positive':
        return 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20';
      case 'opportunity':
        return 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20';
      default:
        return 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800';
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'positive':
        return CheckCircleIcon;
      case 'warning':
        return ExclamationTriangleIcon;
      case 'opportunity':
        return LightBulbIcon;
      default:
        return CheckCircleIcon;
    }
  };

  return (
    <div className="space-y-6">
      {/* Trend Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {trendIndicators.map((indicator, index) => {
          const Icon = indicator.icon;
          return (
            <motion.div
              key={indicator.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-8 h-8 bg-gradient-to-r ${indicator.color} rounded-lg p-1.5`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(indicator.status)}`}>
                  {indicator.status === 'excellent' ? 'ممتاز' : 'جيد'}
                </div>
              </div>
              
              <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {indicator.title}
              </h3>
              
              <div className="flex items-baseline space-x-1 rtl:space-x-reverse mb-2">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {indicator.value}
                </span>
                {indicator.trend === 'up' ? (
                  <ArrowUpIcon className="w-4 h-4 text-green-500" />
                ) : (
                  <ArrowDownIcon className="w-4 h-4 text-red-500" />
                )}
              </div>
              
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {indicator.period}
              </div>
              
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">
                  التوقع: {indicator.prediction}
                </span>
                <span className="text-blue-600 dark:text-blue-400">
                  {indicator.confidence}% ثقة
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Historical Trend Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              الاتجاه التاريخي للأداء
            </h3>
            <select
              value={trendType}
              onChange={(e) => setTrendType(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="revenue">الإيرادات</option>
              <option value="profit">الأرباح</option>
              <option value="customers">العملاء</option>
              <option value="orders">الطلبات</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={historicalData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => 
                  trendType === 'revenue' || trendType === 'profit' 
                    ? `${(value / 1000000).toFixed(1)}م`
                    : value.toLocaleString()
                }
              />
              <Tooltip
                formatter={(value: any) => [
                  trendType === 'revenue' || trendType === 'profit' 
                    ? `${(value / 1000).toLocaleString()} ألف ر.س`
                    : value.toLocaleString(),
                  trendType === 'revenue' ? 'الإيرادات' : 
                  trendType === 'profit' ? 'الأرباح' :
                  trendType === 'customers' ? 'العملاء' : 'الطلبات'
                ]}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Area
                type="monotone"
                dataKey={trendType}
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.1}
                strokeWidth={3}
              />
              <Line
                type="monotone"
                dataKey={trendType}
                stroke="#3B82F6"
                strokeWidth={3}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
              />
              <Brush dataKey="month" height={30} stroke="#3B82F6" />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            التوقعات المستقبلية
          </h3>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={predictions}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}م`}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  `${(value / 1000).toLocaleString()} ألف ر.س`,
                  name === 'predicted' ? 'المتوقع' :
                  name === 'lower' ? 'الحد الأدنى' : 'الحد الأعلى'
                ]}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Area
                type="monotone"
                dataKey="upper"
                stroke="none"
                fill="#3B82F6"
                fillOpacity={0.1}
              />
              <Area
                type="monotone"
                dataKey="lower"
                stroke="none"
                fill="#ffffff"
                fillOpacity={1}
              />
              <Line
                type="monotone"
                dataKey="predicted"
                stroke="#3B82F6"
                strokeWidth={3}
                strokeDasharray="5 5"
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Seasonal Patterns */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          الأنماط الموسمية
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {seasonalPatterns.map((pattern, index) => (
            <motion.div
              key={pattern.season}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {pattern.season}
                </h4>
                <div className="flex items-center">
                  <span className={`text-lg font-bold ${
                    pattern.avgGrowth > 5 ? 'text-green-600' : 
                    pattern.avgGrowth > 2 ? 'text-blue-600' : 'text-yellow-600'
                  }`}>
                    {pattern.avgGrowth > 0 ? '+' : ''}{pattern.avgGrowth}%
                  </span>
                  {pattern.avgGrowth > 0 ? (
                    <ArrowUpIcon className="w-4 h-4 text-green-500 ml-1" />
                  ) : (
                    <ArrowDownIcon className="w-4 h-4 text-red-500 ml-1" />
                  )}
                </div>
              </div>
              
              <div className={`px-2 py-1 text-xs font-medium rounded-full mb-2 ${
                pattern.avgGrowth > 5 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                pattern.avgGrowth > 2 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
              }`}>
                {pattern.pattern}
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {pattern.description}
              </p>
              
              <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                💡 {pattern.recommendation}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Key Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <LightBulbIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
          الرؤى الرئيسية والتوصيات
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {keyInsights.map((insight, index) => {
            const Icon = getInsightIcon(insight.type);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`border rounded-lg p-4 ${getInsightColor(insight.type)}`}
              >
                <div className="flex items-start space-x-3 rtl:space-x-reverse">
                  <Icon className={`w-5 h-5 mt-0.5 ${
                    insight.type === 'positive' ? 'text-green-600' :
                    insight.type === 'warning' ? 'text-yellow-600' : 'text-blue-600'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {insight.title}
                      </h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        insight.impact === 'عالي' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                        insight.impact === 'متوسط' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        تأثير {insight.impact}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                      {insight.description}
                    </p>
                    <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      📋 الإجراء المقترح: {insight.action}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default TrendAnalysis;
